import React, { useState, useCallback } from 'react';
import styled from 'styled-components';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { artworkService, studentService } from '../../services';
import { LoadingSpinner, ServiceOutageIndicator, RetryableError, BulkUploadErrorSummary, BulkUploadResult } from '../ui';
import { ApiError } from '../../services/api';
import { useErrorNotification } from '../../contexts/ErrorNotificationContext';
import { UploadDropzone, FileWithPreview } from './UploadDropzone';
import { FilePreviewGrid } from './FilePreviewGrid';

// Styled Components
const UploadContainer = styled.div`
  display: grid;
  gap: ${({ theme }) => theme.spacing.xl};

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    gap: ${({ theme }) => theme.spacing.mobile.lg};
    padding: 0 ${({ theme }) => theme.spacing.mobile.gutter};
    min-height: 100vh;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.desktop}) {
    gap: ${({ theme }) => theme.spacing.gallery.lg};
    max-width: 1200px;
    margin: 0 auto;
  }
`;

const Section = styled.section`
  background: ${({ theme }) => theme.colors.white};
  padding: ${({ theme }) => theme.spacing.xl};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  box-shadow: ${({ theme }) => theme.shadows.md};

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    background: ${({ theme }) => theme.colors.mobile.surface};
    padding: ${({ theme }) => theme.spacing.mobile.padding};
    border-radius: ${({ theme }) => theme.borderRadius.gallery.comfortable};
    border: 1px solid ${({ theme }) => theme.colors.mobile.border};
  }
`;

const SectionTitle = styled.h2`
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  color: ${({ theme }) => theme.colors.text};
  font-family: ${({ theme }) => theme.typography.fontFamily.heading};
`;

const Form = styled.form`
  display: grid;
  gap: ${({ theme }) => theme.spacing.lg};
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${({ theme }) => theme.spacing.lg};

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

const Label = styled.label`
  margin-bottom: ${({ theme }) => theme.spacing.xs};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.text};
`;

const Select = styled.select<{ $hasError?: boolean }>`
  padding: ${({ theme }) => theme.spacing.md};
  border: 2px solid ${({ theme, $hasError }) =>
    $hasError ? theme.colors.error : theme.colors.gray.medium};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: ${({ theme }) => theme.typography.fontSize.base};
  background-color: ${({ theme }) => theme.colors.white};
  transition: all ${({ theme }) => theme.transitions.fast};

  &:focus {
    outline: none;
    border-color: ${({ theme, $hasError }) =>
      $hasError ? theme.colors.error : theme.colors.primary};
  }
`;

const TagInput = styled.input`
  padding: ${({ theme }) => theme.spacing.sm};
  border: 1px solid ${({ theme }) => theme.colors.gray.medium};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  min-height: 44px;
  width: 100%;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    font-size: 16px;
    padding: ${({ theme }) => theme.spacing.md};
    min-height: 48px;
  }

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const TagsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.xs};
  margin-top: ${({ theme }) => theme.spacing.sm};
`;

const TagChip = styled.div`
  background: ${({ theme }) => theme.colors.primary}20;
  color: ${({ theme }) => theme.colors.primary};
  padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.sm};
  border-radius: ${({ theme }) => theme.borderRadius.full};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
`;

const TagRemove = styled.button`
  background: none;
  border: none;
  color: inherit;
  cursor: pointer;
  min-width: 44px;
  min-height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  border-radius: ${({ theme }) => theme.borderRadius.full};
  transition: all ${({ theme }) => theme.transitions.fast};

  @media (min-width: ${({ theme }) => theme.breakpoints.tablet}) {
    min-width: 24px;
    min-height: 24px;
    padding: 0;
  }

  &:hover {
    background-color: currentColor;
    color: white;
    opacity: 0.9;
  }
`;

const SubmitButton = styled.button<{ $loading?: boolean }>`
  background-color: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.white};
  border: none;
  padding: ${({ theme }) => theme.spacing.md} ${({ theme }) => theme.spacing.xl};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: ${({ theme }) => theme.typography.fontSize.lg};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.normal};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing.sm};

  &:hover:not(:disabled) {
    background-color: ${({ theme }) => theme.colors.secondary};
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
    transform: none;
  }
`;

const SuccessMessage = styled.div<{ $visible: boolean }>`
  background: linear-gradient(135deg, ${({ theme }) => theme.colors.success}15, ${({ theme }) => theme.colors.success}25);
  color: ${({ theme }) => theme.colors.success};
  border: 2px solid ${({ theme }) => theme.colors.success}40;
  padding: ${({ theme }) => theme.spacing.lg} ${({ theme }) => theme.spacing.xl};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  margin-bottom: ${({ theme }) => theme.spacing.xl};
  text-align: center;
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  font-size: ${({ theme }) => theme.typography.fontSize.lg};
  box-shadow: ${({ theme }) => theme.shadows.md};
  transform: ${({ $visible }) => $visible ? 'translateY(0) scale(1)' : 'translateY(-10px) scale(0.95)'};
  opacity: ${({ $visible }) => $visible ? 1 : 0};
  transition: all ${({ theme }) => theme.transitions.normal} ease-out;
  position: relative;
`;

const DismissButton = styled.button`
  position: absolute;
  top: ${({ theme }) => theme.spacing.sm};
  right: ${({ theme }) => theme.spacing.sm};
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.success};
  cursor: pointer;
  font-size: ${({ theme }) => theme.typography.fontSize.lg};
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  opacity: 0.7;
  transition: all ${({ theme }) => theme.transitions.fast};

  &:hover {
    opacity: 1;
    background: ${({ theme }) => theme.colors.success}20;
  }
`;

const ErrorMessage = styled.span`
  color: ${({ theme }) => theme.colors.error};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  margin-top: ${({ theme }) => theme.spacing.xs};
`;

const ErrorContainer = styled.div`
  padding: 16px;
  background-color: #fee;
  border: 1px solid #fcc;
  border-radius: 8px;
  margin-bottom: 16px;
  color: #c33;
`;

// Schema
const schema = yup.object({
  student_id: yup.string().optional(),
});

interface UploadFormData {
  student_id?: string;
}

export function ArtworkUpload() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { showAuthError } = useErrorNotification();
  const [files, setFiles] = useState<FileWithPreview[]>([]);
  const [tags, setTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState('');
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [uploadError, setUploadError] = useState<ApiError | null>(null);
  const [uploadResult, setUploadResult] = useState<BulkUploadResult | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<UploadFormData>({
    resolver: yupResolver(schema),
  });

  // Fetch students for dropdown
  const { data: studentsData, error: studentsError, refetch: refetchStudents } = useQuery({
    queryKey: ['admin', 'students'],
    queryFn: () => studentService.getStudents(1, 100),
    retry: (failureCount, error) => {
      const apiError = error as any;
      if (apiError?.type === 'service_outage' || !apiError?.isRetryable) {
        return false;
      }
      return failureCount < 2;
    },
  });

  const uploadMutation = useMutation({
    mutationFn: async (data: { studentId?: number; files: File[]; tags: string[] }) => {
      setUploadError(null);
      setUploadResult(null);

      const emptyResult: BulkUploadResult = {
        totalFiles: data.files.length,
        successfulUploads: 0,
        failedUploads: 0,
        errors: []
      };

      let batchResult;
      try {
        batchResult = await artworkService.batchUploadArtworks(
          data.files,
          data.studentId ? { student_id: data.studentId } : undefined
        );
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Batch upload failed';
        const failedResult: BulkUploadResult = {
          ...emptyResult,
          failedUploads: data.files.length,
          errors: data.files.map((file) => ({
            fileName: file.name,
            error: message
          }))
        };
        setUploadResult(failedResult);
        throw error;
      }

      const successfulArtworks = batchResult.results
        .filter((result) => result.success && result.artwork)
        .map((result) => result.artwork!);

      const results: BulkUploadResult = {
        ...emptyResult,
        successfulUploads: batchResult.success,
        failedUploads: batchResult.failed,
        errors: batchResult.results
          .filter((result) => !result.success)
          .map((result) => ({
            fileName: result.filename || 'Unknown file',
            error: result.error || 'Unknown error occurred'
          }))
      };

      if (data.tags.length > 0 && successfulArtworks.length > 0) {
        const taggedArtworks = await Promise.allSettled(
          successfulArtworks.map((artwork) =>
            artworkService.addTagsToArtwork(artwork.artwork_id, data.tags)
          )
        );

        taggedArtworks.forEach((result, index) => {
          if (result.status === 'fulfilled') {
            successfulArtworks[index] = result.value;
            return;
          }

          results.errors.push({
            fileName: data.files[index]?.name || `Artwork ${index + 1}`,
            error: result.reason instanceof Error
              ? `Uploaded, but tagging failed: ${result.reason.message}`
              : 'Uploaded, but tagging failed'
          });
        });
      }

      setUploadResult(results);
      return { artworks: successfulArtworks, result: results };
    },
    onSuccess: ({ result }) => {
      // Keep the existing upload invalidations and refresh every mounted
      // artwork/student-derived listing that can include the new record. The
      // homepage's curated membership query is intentionally independent of an
      // upload and does not need to be invalidated here.
      queryClient.invalidateQueries({ queryKey: ['admin', 'artwork-stats'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'recent-artworks'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'artworks'] });
      queryClient.invalidateQueries({ queryKey: ['artworks'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'students'] });
      queryClient.invalidateQueries({ queryKey: ['gallery-artworks'] });
      queryClient.invalidateQueries({ queryKey: ['student-artworks'] });
      queryClient.invalidateQueries({ queryKey: ['student'] });
      queryClient.invalidateQueries({ queryKey: ['visible-students'] });
      queryClient.invalidateQueries({ queryKey: ['recent-artworks-strip'] });
      queryClient.invalidateQueries({ queryKey: ['artists-strip'] });

      setFiles([]);
      setTags([]);
      setTagInput('');
      setUploadError(null);
      reset();

      if (result.failedUploads === 0 && result.errors.length === 0 && result.successfulUploads > 0) {
        setUploadSuccess(true);
        setTimeout(() => setUploadSuccess(false), 8000);
      }
    },
    onError: (error: ApiError) => {
      setUploadError(error);
      setUploadSuccess(false);

      if (error.type === 'auth') {
        showAuthError(
          'Your session has expired. Please log in again to upload artwork.',
          () => { window.location.href = '/admin/login'; }
        );
      }
    },
  });

  const handleFilesAdded = useCallback((acceptedFiles: File[]) => {
    const newFiles = acceptedFiles.map(file => Object.assign(file, {
      preview: URL.createObjectURL(file)
    }));
    setFiles(prev => [...prev, ...newFiles]);
  }, []);

  const handleFileRemove = useCallback((index: number) => {
    setFiles(prev => {
      const newFiles = [...prev];
      URL.revokeObjectURL(newFiles[index].preview);
      newFiles.splice(index, 1);
      return newFiles;
    });
  }, []);

  const handleTagAdd = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && tagInput.trim()) {
      e.preventDefault();
      const newTag = tagInput.trim().toLowerCase();
      if (!tags.includes(newTag)) {
        setTags(prev => [...prev, newTag]);
      }
      setTagInput('');
    }
  };

  const handleTagRemove = (tagToRemove: string) => {
    setTags(prev => prev.filter(tag => tag !== tagToRemove));
  };

  const onSubmit = async (data: UploadFormData) => {
    if (files.length === 0) {
      alert('Please select files to upload');
      return;
    }

    const studentId = data.student_id && data.student_id !== '' && !isNaN(Number(data.student_id))
      ? Number(data.student_id)
      : undefined;

    await uploadMutation.mutateAsync({
      studentId,
      files: files,
      tags: tags,
    });
  };

  const studentsApiError = studentsError as any;
  const hasServiceOutage = studentsApiError && (studentsApiError.type === 'service_outage' || studentsApiError.type === 'network');

  return (
    <UploadContainer>
      {hasServiceOutage && (
        <ServiceOutageIndicator
          error={studentsApiError}
          variant="banner"
          onRetry={refetchStudents}
          customTitle="Backend Service Unavailable"
          customMessage="Some features may not work properly while our backend is experiencing issues."
        />
      )}

      <Section>
        <SectionTitle>Upload Student Artwork</SectionTitle>

        {uploadSuccess && (
          <SuccessMessage $visible={uploadSuccess}>
            Upload Complete! Your artworks have been successfully added to the gallery.
            <DismissButton onClick={() => setUploadSuccess(false)} aria-label="Dismiss success message">
              x
            </DismissButton>
          </SuccessMessage>
        )}

        {uploadResult && uploadResult.totalFiles > 0 && (
          <BulkUploadErrorSummary
            result={uploadResult}
            onRetryFailed={() => {
              if (uploadResult.errors.length > 0) {
                const failedFileNames = uploadResult.errors.map(e => e.fileName);
                const failedFiles = files.filter(file => failedFileNames.includes(file.name));

                if (failedFiles.length > 0) {
                  setUploadResult(null);
                  const currentStudentId = document.querySelector<HTMLSelectElement>('[name="student_id"]')?.value;

                  uploadMutation.mutateAsync({
                    studentId: currentStudentId && currentStudentId !== '' && !isNaN(Number(currentStudentId))
                      ? Number(currentStudentId)
                      : undefined,
                    files: failedFiles,
                    tags: tags,
                  });
                }
              }
            }}
            onDismiss={() => {
              setUploadResult(null);
              setUploadError(null);
            }}
            onViewSuccessful={uploadResult.successfulUploads > 0 ? () => {
              navigate('/admin');
            } : undefined}
            position="relative"
          />
        )}

        <UploadDropzone
          files={files}
          onFilesAdded={handleFilesAdded}
        />

        <FilePreviewGrid
          files={files}
          onRemove={handleFileRemove}
        />
      </Section>

      <Section>
        <SectionTitle>Artwork Details</SectionTitle>
        <Form onSubmit={handleSubmit(onSubmit)}>
          <FormGrid>
            <FormGroup>
              <Label htmlFor="student_id">Student (Optional)</Label>
              <Select
                {...register('student_id')}
                id="student_id"
                $hasError={!!errors.student_id}
                disabled={hasServiceOutage}
              >
                <option value="">
                  {hasServiceOutage ? 'Service unavailable' : 'Select a student'}
                </option>
                {!hasServiceOutage && studentsData?.students?.map((student) => (
                  <option key={student.student_id} value={student.student_id}>
                    {student.name}
                  </option>
                ))}
              </Select>
              {errors.student_id && (
                <ErrorMessage>{errors.student_id.message}</ErrorMessage>
              )}
            </FormGroup>

            <FormGroup>
              <Label htmlFor="tags">Tags</Label>
              <TagInput
                id="tags"
                type="text"
                placeholder="Type a tag and press Enter"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={handleTagAdd}
                autoComplete="off"
              />
              {tags.length > 0 && (
                <TagsContainer>
                  {tags.map((tag) => (
                    <TagChip key={tag}>
                      {tag}
                      <TagRemove
                        onClick={() => handleTagRemove(tag)}
                        aria-label={`Remove tag ${tag}`}
                      >
                        x
                      </TagRemove>
                    </TagChip>
                  ))}
                </TagsContainer>
              )}
            </FormGroup>
          </FormGrid>

          {uploadError && (
            <>
              {(uploadError.type === 'service_outage' || uploadError.type === 'network') ? (
                <ServiceOutageIndicator
                  error={uploadError}
                  variant="card"
                  onRetry={() => {
                    setUploadError(null);
                    uploadMutation.reset();
                  }}
                  customTitle="Upload Failed - Service Unavailable"
                  customMessage="Your upload couldn't be completed. Please try again once service is restored."
                />
              ) : uploadError.isRetryable ? (
                <RetryableError
                  error={uploadError}
                  onRetry={async () => {
                    setUploadError(null);
                    uploadMutation.reset();
                  }}
                  variant="compact"
                  onDismiss={() => setUploadError(null)}
                />
              ) : (
                <ErrorContainer>
                  <strong>Upload Failed:</strong> {uploadError.message}
                  {uploadError.errors && (
                    <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                      {Object.entries(uploadError.errors).map(([field, fieldErrors]) =>
                        fieldErrors.map((fieldError, index) => (
                          <li key={`${field}-${index}`}>{field}: {fieldError}</li>
                        ))
                      )}
                    </ul>
                  )}
                  <button
                    onClick={() => setUploadError(null)}
                    style={{
                      marginTop: '8px',
                      padding: '4px 8px',
                      border: '1px solid #999',
                      background: 'white',
                      borderRadius: '4px',
                      cursor: 'pointer'
                    }}
                  >
                    Dismiss
                  </button>
                </ErrorContainer>
              )}
            </>
          )}

          <SubmitButton
            type="submit"
            disabled={uploadMutation.isPending || files.length === 0 || hasServiceOutage}
            $loading={uploadMutation.isPending}
          >
            {uploadMutation.isPending ? (
              <>
                <LoadingSpinner size="sm" color="white" />
                Uploading...
              </>
            ) : hasServiceOutage ? (
              'Service Unavailable'
            ) : (
              `Upload ${files.length} file(s)`
            )}
          </SubmitButton>
        </Form>
      </Section>
    </UploadContainer>
  );
}
